import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import api, { deploymentApi } from '../../../lib/api'
import type { DeploymentTask } from '../../../lib/api'
import { formatCurrency } from '../../../lib/format'
import { usePermissions } from '../../../hooks/useAuth'
import { MERCHANT_NAV_ROLES } from '../../../lib/navAccess'
import { RelatedAccountsPanel } from './RelatedAccountsPanel'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import { CreateTicketModal } from '../../tickets/CreateTicketModal'
import { DocumentCenter } from '../../lead/DocumentCenter'
import { PendingDocumentRequests } from '../../lead/PendingDocumentRequests'
import { AccountActivityPanel } from '../AccountActivityPanel'
import { AppointmentsPanel } from '../AppointmentsPanel'
import { NotesPanel } from '../NotesPanel'
import { AuthorizeNetBoardingWizard } from '../../merchant/AuthorizeNetBoardingWizard'
import { EquipmentAssetModal } from '../../merchant/EquipmentAssetModal'
import type { EquipmentAssetPayload } from '../../merchant/EquipmentAssetModal'
import { EquipmentAssetsPanel } from '../../merchant/EquipmentAssetsPanel'
import {
  buildDejavooConfigPayload,
  DejavooFileBuilder,
  emptyDejavooFileBuilderState,
  initializeDejavooFileBuilderState,
  validateDejavooFileBuilderState,
  type DejavooFileBuilderState,
  type DejavooParamCatalogResponse,
} from '../../merchant/DejavooFileBuilder'
import { GatewayServicesPanel } from '../../merchant/GatewayServicesPanel'
import { MerchantMaintenanceSection } from '../../merchant/MerchantMaintenanceSection'
import { MerchantReportingSection } from '../../merchant/MerchantReportingSection'
import { MerchantStatementsSection } from '../../merchant/MerchantStatementsSection'
import { MiniStat, PageHeader, SurfaceCard } from '../../ui'
import {
  chargebackStatusClass,
  daysToDeadlineLabel,
  ticketPriorityClass,
  ticketStatusClass,
} from '../../../pages/merchants/merchantFormatters'
import type { GatewayServiceDefinition, MerchantEquipmentAsset } from '../../../pages/merchants/merchantFormatters'
import {
  useMerchantChargebacks,
  useMerchantContext,
  useMerchantDeploymentTasks,
  useMerchantEquipmentAssets,
  useMerchantTickets,
  useMerchantTransactions,
} from '../../../pages/merchants/useMerchantData'
import type { Merchant } from '../../../pages/merchants/useMerchantData'

interface GatewayProvisioningProfile {
  business_name: string
  business_email: string
  business_phone: string
  address: string
  city: string
  state: string
  zip: string
  website: string
  owner_first_name: string
  owner_last_name: string
  owner_email: string
  owner_phone: string
  bank_name: string
  routing_number: string
  account_number: string
  nmi_platform: 'fdms' | 'fdmso'
  nmi_username: string
  nmi_processor_mid: string
  nmi_processor_tid: string
  nmi_mcc: string
}

interface DejavooProfile {
  device: 'qd2' | 'qd4'
  processor: 'omaha' | 'nashville'
  business_type: 'retail' | 'restaurant'
  pricing: 'interchange' | 'flat_rate'
  description: string
}

interface AddGatewayServicePayload {
  service: GatewayServiceDefinition
  provisioningProfile?: GatewayProvisioningProfile
  dejavooProfile?: DejavooProfile
  dejavooConfig?: Record<string, string>
}

interface MerchantAccountWorkspaceProps {
  merchantId: string | undefined | null
  showHeader?: boolean
  showSharedSections?: boolean
  showRelatedAccounts?: boolean
}

const DEJAVOO_SOURCE_PROFILE_OPTIONS = [
  { key: 'qd2.omaha.retail.interchange', device: 'qd2', processor: 'omaha', business_type: 'retail', pricing: 'interchange', sourceTpn: '101QD2RETOIN', label: 'QD2 Omaha Retail Interchange' },
  { key: 'qd2.omaha.retail.flat_rate', device: 'qd2', processor: 'omaha', business_type: 'retail', pricing: 'flat_rate', sourceTpn: '101QD2RETOFR', label: 'QD2 Omaha Retail Flat Rate' },
  { key: 'qd2.omaha.restaurant.interchange', device: 'qd2', processor: 'omaha', business_type: 'restaurant', pricing: 'interchange', sourceTpn: '101QD2RESOIN', label: 'QD2 Omaha Restaurant Interchange' },
  { key: 'qd2.omaha.restaurant.flat_rate', device: 'qd2', processor: 'omaha', business_type: 'restaurant', pricing: 'flat_rate', sourceTpn: '101QD2RESOFR', label: 'QD2 Omaha Restaurant Flat Rate' },
  { key: 'qd2.nashville.retail.interchange', device: 'qd2', processor: 'nashville', business_type: 'retail', pricing: 'interchange', sourceTpn: '', label: 'QD2 Nashville Retail Interchange' },
  { key: 'qd2.nashville.retail.flat_rate', device: 'qd2', processor: 'nashville', business_type: 'retail', pricing: 'flat_rate', sourceTpn: '', label: 'QD2 Nashville Retail Flat Rate' },
  { key: 'qd2.nashville.restaurant.interchange', device: 'qd2', processor: 'nashville', business_type: 'restaurant', pricing: 'interchange', sourceTpn: '', label: 'QD2 Nashville Restaurant Interchange' },
  { key: 'qd2.nashville.restaurant.flat_rate', device: 'qd2', processor: 'nashville', business_type: 'restaurant', pricing: 'flat_rate', sourceTpn: '', label: 'QD2 Nashville Restaurant Flat Rate' },
  { key: 'qd4.omaha.retail.interchange', device: 'qd4', processor: 'omaha', business_type: 'retail', pricing: 'interchange', sourceTpn: '101QD4RETOIN', label: 'QD4 Omaha Retail Interchange' },
  { key: 'qd4.omaha.retail.flat_rate', device: 'qd4', processor: 'omaha', business_type: 'retail', pricing: 'flat_rate', sourceTpn: '101QD4RETOFR', label: 'QD4 Omaha Retail Flat Rate' },
  { key: 'qd4.omaha.restaurant.interchange', device: 'qd4', processor: 'omaha', business_type: 'restaurant', pricing: 'interchange', sourceTpn: '101QD4RESOIN', label: 'QD4 Omaha Restaurant Interchange' },
  { key: 'qd4.omaha.restaurant.flat_rate', device: 'qd4', processor: 'omaha', business_type: 'restaurant', pricing: 'flat_rate', sourceTpn: '101QD4RESOFR', label: 'QD4 Omaha Restaurant Flat Rate' },
  { key: 'qd4.nashville.retail.interchange', device: 'qd4', processor: 'nashville', business_type: 'retail', pricing: 'interchange', sourceTpn: '', label: 'QD4 Nashville Retail Interchange' },
  { key: 'qd4.nashville.retail.flat_rate', device: 'qd4', processor: 'nashville', business_type: 'retail', pricing: 'flat_rate', sourceTpn: '', label: 'QD4 Nashville Retail Flat Rate' },
  { key: 'qd4.nashville.restaurant.interchange', device: 'qd4', processor: 'nashville', business_type: 'restaurant', pricing: 'interchange', sourceTpn: '', label: 'QD4 Nashville Restaurant Interchange' },
  { key: 'qd4.nashville.restaurant.flat_rate', device: 'qd4', processor: 'nashville', business_type: 'restaurant', pricing: 'flat_rate', sourceTpn: '', label: 'QD4 Nashville Restaurant Flat Rate' },
] as const

function emptyGatewayProvisioningProfile(merchant?: Merchant | null): GatewayProvisioningProfile {
  return {
    business_name: merchant?.legal_name || merchant?.dba_name || '',
    business_email: merchant?.business_email || '',
    business_phone: merchant?.business_phone || '',
    address: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    owner_phone: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    nmi_platform: 'fdms',
    nmi_username: '',
    nmi_processor_mid: merchant?.fiserv_mid || '',
    nmi_processor_tid: '',
    nmi_mcc: '',
  }
}

function normalizeGatewayProvisioningProfile(profile: GatewayProvisioningProfile) {
  return {
    business_name: profile.business_name.trim(),
    business_email: profile.business_email.trim(),
    business_phone: profile.business_phone.trim(),
    address: profile.address.trim(),
    city: profile.city.trim(),
    state: profile.state.trim().toUpperCase(),
    zip: profile.zip.trim(),
    website: profile.website.trim() || null,
    owner_first_name: profile.owner_first_name.trim(),
    owner_last_name: profile.owner_last_name.trim(),
    owner_email: profile.owner_email.trim(),
    owner_phone: profile.owner_phone.trim(),
    bank_name: profile.bank_name.trim(),
    routing_number: profile.routing_number.replace(/\D+/g, ''),
    account_number: profile.account_number.trim(),
    nmi_platform: profile.nmi_platform,
    nmi_username: profile.nmi_username.trim(),
    nmi_processor_mid: profile.nmi_processor_mid.replace(/\D+/g, ''),
    nmi_processor_tid: profile.nmi_platform === 'fdms' ? profile.nmi_processor_tid.trim() : '',
    nmi_mcc: profile.nmi_mcc.replace(/\D+/g, ''),
  }
}

function emptyDejavooProfile(): DejavooProfile {
  return {
    device: 'qd2',
    processor: 'omaha',
    business_type: 'retail',
    pricing: 'interchange',
    description: '',
  }
}

function normalizeDejavooProfile(profile: DejavooProfile) {
  return {
    device: profile.device,
    processor: profile.processor,
    business_type: profile.business_type,
    pricing: profile.pricing,
    description: profile.description.trim(),
  }
}

function dejavooComboKey(profile: DejavooProfile): string {
  return `${profile.device}.${profile.processor}.${profile.business_type}.${profile.pricing}`
}

function requiresGatewayProvisioningProfile(error: unknown): boolean {
  const responseData = (error as {
    response?: {
      data?: {
        message?: string
        requires_provisioning_profile?: boolean
      }
    }
  }).response?.data

  return responseData?.requires_provisioning_profile === true
    || responseData?.message?.toLowerCase().includes('gateway provisioning requires business, owner, and banking details') === true
}


export function MerchantAccountWorkspace({
  merchantId,
  showHeader = true,
  showSharedSections = true,
  showRelatedAccounts = true,
}: MerchantAccountWorkspaceProps) {
  const queryClient = useQueryClient()
  const { canManageGatewayServices, canViewDeploymentTasks, canRetryDeploymentTasks, canViewDataHealth, canCreateTickets, canViewTickets, canViewMerchantMaintenance, canEditMerchantMaintenance, canViewMerchantReporting, canViewChargebacks, hasRole } = usePermissions()
  const showDataHealth = canViewDataHealth()
  const canManageGatewayServiceActions = canManageGatewayServices()
  const salesRep = hasRole('sales_rep')
  const showGatewayServices = hasRole([...MERCHANT_NAV_ROLES])
  const showDeploymentTasks = canViewDeploymentTasks()
  const canRetryDeploymentTaskActions = canRetryDeploymentTasks()
  const showChargebacks = canViewChargebacks()
  const showTicketList = canViewTickets()
  const showCreateTicket = canCreateTickets()
  const showMerchantMaintenance = canViewMerchantMaintenance()
  const showMerchantReporting = canViewMerchantReporting()
  const canManageEquipmentAssets = canEditMerchantMaintenance()
  const selectedId = merchantId ?? null
  const { data: accountContext, isLoading } = useMerchantContext(selectedId)
  const selected = accountContext?.merchant ?? null

  const { data: txns } = useMerchantTransactions(selectedId)
  const { data: cbs } = useMerchantChargebacks(showChargebacks ? selectedId : null)
  const { data: tickets } = useMerchantTickets(showTicketList ? selectedId : null)
  const { data: deploymentTasks } = useMerchantDeploymentTasks(showGatewayServices && showDeploymentTasks ? selectedId : null)
  const { data: equipmentAssets } = useMerchantEquipmentAssets(selectedId, showMerchantMaintenance)
  const [confirmingService, setConfirmingService] = useState<GatewayServiceDefinition | null>(null)
  const [profileService, setProfileService] = useState<GatewayServiceDefinition | null>(null)
  const [dejavooService, setDejavooService] = useState<GatewayServiceDefinition | null>(null)
  const [showAuthorizeNetWizard, setShowAuthorizeNetWizard] = useState(false)
  const [gatewayProfileForm, setGatewayProfileForm] = useState<GatewayProvisioningProfile>(() => emptyGatewayProvisioningProfile())
  const [dejavooForm, setDejavooForm] = useState<DejavooProfile>(() => emptyDejavooProfile())
  const [dejavooFileBuilderState, setDejavooFileBuilderState] = useState<DejavooFileBuilderState>(() => emptyDejavooFileBuilderState())
  const [profileError, setProfileError] = useState<string | null>(null)
  const [dejavooError, setDejavooError] = useState<string | null>(null)
  const [gatewayError, setGatewayError] = useState<string | null>(null)
  const [showAddAssetModal, setShowAddAssetModal] = useState(false)
  const [assetError, setAssetError] = useState<string | null>(null)
  const [editingAsset, setEditingAsset] = useState<MerchantEquipmentAsset | null>(null)
  const [assetForm, setAssetForm] = useState<EquipmentAssetPayload>({
    asset_type: 'terminal',
    make: '',
    model: '',
    serial_number: '',
    terminal_id: '',
    tracking_number: '',
    status: 'ready_to_build',
    notes: '',
  })
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false)
  const dejavooParamCatalogQuery = useQuery({
    queryKey: ['merchant-dejavoo-param-catalog', selectedId],
    enabled: Boolean(selectedId && dejavooService),
    queryFn: async () => {
      const response = await api.get<DejavooParamCatalogResponse>(`/merchants/${selectedId}/dejavoo/param-catalog`)
      return response.data
    },
  })

  useEffect(() => {
    if (
      dejavooService
      && dejavooParamCatalogQuery.data
      && Object.keys(dejavooFileBuilderState.values).length === 0
    ) {
      setDejavooFileBuilderState(initializeDejavooFileBuilderState(dejavooParamCatalogQuery.data))
    }
  }, [dejavooService, dejavooParamCatalogQuery.data, dejavooFileBuilderState.values])

  const addServiceMutation = useMutation({
    mutationFn: async ({ service, provisioningProfile, dejavooProfile, dejavooConfig }: AddGatewayServicePayload) => {
      if (!selected) throw new Error('No merchant selected')

      const payload: Record<string, unknown> = {
        adapter_type: service.adapterType,
      }

      if (provisioningProfile) {
        payload.provisioning_profile = normalizeGatewayProvisioningProfile(provisioningProfile)
      }

      if (dejavooProfile) {
        payload.dejavoo_profile = normalizeDejavooProfile(dejavooProfile)
      }

      if (dejavooConfig && Object.keys(dejavooConfig).length > 0) {
        payload.dejavoo_config = dejavooConfig
      }

      const response = await api.post(`/merchants/${selected.id}/services`, payload)
      return response.data
    },
    onSuccess: async () => {
      setConfirmingService(null)
      setProfileService(null)
      setDejavooService(null)
      setGatewayProfileForm(emptyGatewayProvisioningProfile())
      setDejavooForm(emptyDejavooProfile())
      setDejavooFileBuilderState(emptyDejavooFileBuilderState())
      setGatewayError(null)
      setProfileError(null)
      setDejavooError(null)
      await queryClient.invalidateQueries({ queryKey: ['merchants'] })
      await queryClient.invalidateQueries({ queryKey: ['merchant-context', selectedId] })
      await queryClient.invalidateQueries({ queryKey: ['merchant-deployment-tasks', selectedId] })
      await queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
    },
    onError: (error, variables) => {
      if (requiresGatewayProvisioningProfile(error)) {
        setConfirmingService(null)
        setProfileService(variables.service)
        setGatewayProfileForm(emptyGatewayProvisioningProfile(selected))
        setGatewayError(null)
        setProfileError(null)
        return
      }

      if (variables.provisioningProfile) {
        setProfileError(apiErrorMessage(error, 'Unable to add gateway service.'))
        return
      }

      if (variables.dejavooProfile) {
        setDejavooError(apiErrorMessage(error, 'Unable to add Dejavoo terminal.'))
        return
      }

      setGatewayError(apiErrorMessage(error, 'Unable to add gateway service.'))
    },
  })

  const retryTaskMutation = useMutation({
    mutationFn: (taskId: string) => deploymentApi.retry(taskId),
    onSuccess: async () => {
      setGatewayError(null)
      await queryClient.invalidateQueries({ queryKey: ['merchant-deployment-tasks', selectedId] })
      await queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
    },
    onError: (error) => {
      setGatewayError(apiErrorMessage(error, 'Unable to retry gateway provisioning.'))
    },
  })

  const cancelTaskMutation = useMutation({
    mutationFn: async (task: DeploymentTask) => {
      if (!selected) throw new Error('No merchant selected')
      const response = await api.post(`/merchants/${selected.id}/services/${task.id}/cancel`)
      return response.data
    },
    onSuccess: async () => {
      setGatewayError(null)
      await queryClient.invalidateQueries({ queryKey: ['merchant-deployment-tasks', selectedId] })
      await queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
      await queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: (error) => {
      setGatewayError(apiErrorMessage(error, 'Unable to cancel gateway service.'))
    },
  })

  const addAssetMutation = useMutation({
    mutationFn: async (payload: EquipmentAssetPayload) => {
      if (!selected) throw new Error('No merchant selected')
      const response = await api.post(`/merchants/${selected.id}/equipment-assets`, {
        asset_type: payload.asset_type,
        make: payload.make.trim() || null,
        model: payload.model.trim() || null,
        serial_number: payload.serial_number.trim() || null,
        terminal_id: payload.terminal_id.trim() || null,
        tracking_number: payload.tracking_number.trim() || null,
        status: payload.status,
        notes: payload.notes.trim() || null,
      })
      return response.data
    },
    onSuccess: async () => {
      setShowAddAssetModal(false)
      setAssetError(null)
      setAssetForm({
        asset_type: 'terminal',
        make: '',
        model: '',
        serial_number: '',
        terminal_id: '',
        tracking_number: '',
        status: 'ready_to_build',
        notes: '',
      })
      await queryClient.invalidateQueries({ queryKey: ['merchant-equipment-assets', selectedId] })
    },
    onError: (error) => {
      setAssetError(apiErrorMessage(error, 'Unable to add equipment asset.'))
    },
  })

  const updateAssetMutation = useMutation({
    mutationFn: async (payload: EquipmentAssetPayload) => {
      if (!selected || !editingAsset) throw new Error('No equipment asset selected')
      const response = await api.patch(`/merchants/${selected.id}/equipment-assets/${editingAsset.id}`, {
        asset_type: payload.asset_type,
        make: payload.make.trim() || null,
        model: payload.model.trim() || null,
        serial_number: payload.serial_number.trim() || null,
        terminal_id: payload.terminal_id.trim() || null,
        tracking_number: payload.tracking_number.trim() || null,
        status: payload.status,
        notes: payload.notes.trim() || null,
      })
      return response.data
    },
    onSuccess: async () => {
      setShowAddAssetModal(false)
      setEditingAsset(null)
      setAssetError(null)
      await queryClient.invalidateQueries({ queryKey: ['merchant-equipment-assets', selectedId] })
    },
    onError: (error) => {
      setAssetError(apiErrorMessage(error, 'Unable to update equipment asset.'))
    },
  })

  const deleteAssetMutation = useMutation({
    mutationFn: async (asset: MerchantEquipmentAsset) => {
      if (!selected) throw new Error('No merchant selected')
      const response = await api.delete(`/merchants/${selected.id}/equipment-assets/${asset.id}`)
      return response.data
    },
    onSuccess: async () => {
      setAssetError(null)
      await queryClient.invalidateQueries({ queryKey: ['merchant-equipment-assets', selectedId] })
    },
    onError: (error) => {
      setAssetError(apiErrorMessage(error, 'Unable to delete equipment asset.'))
    },
  })

  const dataHealthLabel = (merchant: Merchant): string => {
    if (merchant.last_transaction_sync_status === 'failed') return 'Data unavailable'
    if (merchant.last_transaction_sync_status === 'skipped') return 'Data pending'
    if (!merchant.last_transaction_sync_at) return 'Data pending'

    const syncedAt = new Date(merchant.last_transaction_sync_at)
    const diffMinutes = Math.max(0, Math.round((Date.now() - syncedAt.getTime()) / 60000))
    if (diffMinutes < 60) return `Updated ${diffMinutes} min ago`
    const diffHours = Math.round(diffMinutes / 60)
    return `Updated ${diffHours} hr ago`
  }

  if (isLoading) {
    return <p className="text-gray-500 py-8 text-center">Loading merchants...</p>
  }

  if (!selected) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Merchant not found"
          subtitle="The merchant record may have been removed or you may not have access."
          action={<Link to="/merchants" className="text-sm font-semibold text-blue-700 hover:text-blue-900">Back to Merchants</Link>}
        />
      </div>
    )
  }

  const leadContext = accountContext?.lead ?? null
  const applicationContext = accountContext?.application ?? null

  return (
    <div className="space-y-6">
      {showHeader && (
        <>
          <PageHeader
            title={selected.dba_name}
            subtitle={selected.legal_name ?? undefined}
            action={<Link to="/merchants" className="text-sm font-semibold text-blue-700 hover:text-blue-900">Back to Merchants</Link>}
          />

          <SurfaceCard padding="p-5">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
                    {selected.status}
                  </span>
                  <span className="text-sm text-slate-500">MID: {selected.fiserv_mid ?? '—'}</span>
                </div>
                {leadContext ? (
                  <div className="text-sm text-slate-600">
                    Originating lead:{' '}
                    <Link to={`/leads/${leadContext.id}`} className="font-semibold text-blue-700 hover:text-blue-900">
                      {leadContext.dba_name ?? 'View lead'}
                    </Link>
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold capitalize text-slate-600">
                      {leadContext.stage.replace(/_/g, ' ')}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Imported — no pipeline history</p>
                )}
                {applicationContext && (
                  <p className="text-sm text-slate-500">
                    Application {applicationContext.fiserv_as_ref_id ? `AS Ref ${applicationContext.fiserv_as_ref_id}` : applicationContext.id}
                    {applicationContext.fiserv_status ? ` · ${applicationContext.fiserv_status}` : ''}
                  </p>
                )}
              </div>
              <div className="grid min-w-64 gap-2 text-sm text-slate-600">
                <div>
                  <span className="font-semibold text-slate-700">Primary:</span>{' '}
                  {leadContext?.assigned_to?.name ?? 'Unassigned'}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Secondary:</span>{' '}
                  {leadContext?.assigned_to_secondary?.name ?? 'Unassigned'}
                </div>
              </div>
            </div>
          </SurfaceCard>
        </>
      )}

      {showRelatedAccounts && <RelatedAccountsPanel merchantId={selected.id} />}

              {/* Portfolio metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Monthly Volume', value: formatCurrency(selected.monthly_volume) ?? '$0' },
                  { label: 'YTD Volume', value: formatCurrency(selected.ytd_volume) ?? '$0' },
                  { label: 'CB Ratio', value: selected.chargeback_ratio != null ? `${(selected.chargeback_ratio * 100).toFixed(2)}%` : '0%' },
                  { label: 'Risk Tier', value: selected.risk_tier ?? 'Standard' },
                  ...(showDataHealth ? [{ label: 'Data Health', value: dataHealthLabel(selected) }] : []),
                ].map((stat, index) => (
                  <MiniStat
                    key={stat.label}
                    label={stat.label}
                    value={stat.value}
                    tone={index === 0 ? 'emerald' : index === 1 ? 'blue' : index === 2 ? 'rose' : 'slate'}
                  />
                ))}
              </div>

              {showDataHealth && selected.last_transaction_sync_error && (
                <Card>
                  <CardContent className="border-l-4 border-rose-400 bg-rose-50 py-4">
                    <p className="text-sm font-semibold text-rose-900">Data Health Alert</p>
                    <p className="mt-1 text-sm text-rose-800">{selected.last_transaction_sync_error}</p>
                  </CardContent>
                </Card>
              )}

              {showMerchantMaintenance && (
                <MerchantMaintenanceSection merchant={selected} />
              )}

              {showMerchantReporting && (
                <MerchantReportingSection merchant={selected} />
              )}

              {showMerchantReporting && (
                <MerchantStatementsSection merchant={selected} />
              )}

              {showMerchantMaintenance && (
                <EquipmentAssetsPanel
                  equipmentAssets={equipmentAssets}
                  canManageEquipmentAssets={canManageEquipmentAssets}
                  deletePending={deleteAssetMutation.isPending}
                  onAddAsset={() => {
                    setAssetError(null)
                    setEditingAsset(null)
                    setAssetForm({
                      asset_type: 'terminal',
                      make: '',
                      model: '',
                      serial_number: '',
                      terminal_id: '',
                      tracking_number: '',
                      status: 'ready_to_build',
                      notes: '',
                    })
                    setShowAddAssetModal(true)
                  }}
                  onEditAsset={(asset) => {
                    setAssetError(null)
                    setEditingAsset(asset)
                    setAssetForm({
                      asset_type: asset.asset_type,
                      make: asset.make ?? '',
                      model: asset.model ?? '',
                      serial_number: asset.serial_number ?? '',
                      terminal_id: asset.terminal_id ?? '',
                      tracking_number: asset.tracking_number ?? '',
                      status: asset.status,
                      notes: asset.notes ?? '',
                    })
                    setShowAddAssetModal(true)
                  }}
                  onDeleteAsset={(asset) => {
                    if (window.confirm('Delete this equipment asset? This only removes the AGMS manual asset record.')) {
                      deleteAssetMutation.mutate(asset)
                    }
                  }}
                />
              )}

              {showGatewayServices && (
                <GatewayServicesPanel
                  merchantId={selected.id}
                  deploymentTasks={deploymentTasks}
                  gatewayError={gatewayError}
                  retryPending={retryTaskMutation.isPending}
                  cancelPending={cancelTaskMutation.isPending}
                  canManageGatewayServices={canManageGatewayServiceActions}
                  canRetryDeploymentTasks={canRetryDeploymentTaskActions}
                  canManageNmiStatus={canManageGatewayServiceActions}
                  onAddService={(service) => {
                    setGatewayError(null)
                    setProfileError(null)
                    setDejavooError(null)
                    if (service.adapterType === 'service_authorize_net') {
                      setShowAuthorizeNetWizard(true)
                      return
                    }
                    if (service.adapterType === 'terminal_dejavoo') {
                      setDejavooForm(emptyDejavooProfile())
                      setDejavooFileBuilderState(emptyDejavooFileBuilderState())
                      setDejavooService(service)
                      return
                    }
                    if (service.adapterType === 'service_nmi' || !selected.application_id) {
                      setGatewayProfileForm(emptyGatewayProvisioningProfile(selected))
                      setProfileService(service)
                      return
                    }
                    setConfirmingService(service)
                  }}
                  onRetry={(taskId) => retryTaskMutation.mutate(taskId)}
                  onCancel={(task) => cancelTaskMutation.mutate(task)}
                />
              )}

              {showSharedSections && (
                <>
                  <AppointmentsPanel anchor={{ kind: 'merchant', id: selected.id }} />

                  <AccountActivityPanel anchor={{ kind: 'merchant', id: selected.id }} />

                  {!salesRep && (
                    <>
                      <DocumentCenter context={{ kind: 'merchant', merchant: selected }} />

                      <PendingDocumentRequests anchor={{ kind: 'merchant', id: selected.id }} />
                    </>
                  )}

                  <NotesPanel anchor={{ kind: 'merchant', id: selected.id }} />
                </>
              )}

              {(showTicketList || showCreateTicket) && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle>Tickets</CardTitle>
                      {showCreateTicket && (
                        <Button variant="secondary" onClick={() => setShowCreateTicketModal(true)}>
                          Create Ticket
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  {showTicketList && (
                  <CardContent>
                    {tickets && tickets.data.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Subject</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Assigned</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Priority</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {tickets.data.map((ticket) => (
                              <tr key={ticket.id} className="hover:bg-blue-50/60">
                                <td className="px-3 py-2">
                                  <Link to={`/tickets/${ticket.ticket_number}`} className="font-semibold text-blue-700 hover:text-blue-900">
                                    {ticket.ticket_number}: {ticket.subject}
                                  </Link>
                                  <div className="text-xs text-gray-500 capitalize">{ticket.category}</div>
                                </td>
                                <td className="px-3 py-2 text-gray-700">{ticket.assigned_user?.name ?? 'Unassigned'}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${ticketPriorityClass(ticket.priority)}`}>
                                    {ticket.priority}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${ticketStatusClass(ticket.status)}`}>
                                    {ticket.status.replace('_', ' ')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="py-4 text-center text-sm text-gray-500">No tickets on file</p>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Transactions */}
              <Card>
                <CardHeader><CardTitle>Recent Transactions</CardTitle></CardHeader>
                <CardContent>
                  {txns && txns.data.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Card</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {txns.data.map((t) => (
                            <tr key={t.id}>
                              <td className="px-3 py-2 text-gray-700">{t.txn_date}</td>
                              <td className="px-3 py-2 text-gray-700 capitalize">{t.txn_type}</td>
                              <td className="px-3 py-2 text-gray-500">{t.card_type ?? '—'}</td>
                              <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                ${parseFloat(t.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-gray-500 capitalize">{t.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 py-4 text-center text-sm">No transactions synced yet</p>
                  )}
                </CardContent>
              </Card>

      {showChargebacks && (
      <Card>
        <CardHeader><CardTitle>Chargebacks</CardTitle></CardHeader>
        <CardContent>
          {cbs && cbs.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Case ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Due</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cbs.data.map((cb) => (
                    <tr key={cb.id} className="hover:bg-blue-50/60">
                      <td className="px-3 py-2">
                        <Link to={`/chargebacks/${cb.id}`} className="font-mono text-xs font-semibold text-blue-700 hover:text-blue-900">
                          {cb.fiserv_case_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-gray-700 capitalize">{cb.case_type}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-medium">
                        ${parseFloat(cb.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-gray-700">{cb.response_due_at ?? '—'}</div>
                        <div className="text-xs text-gray-500">{daysToDeadlineLabel(cb.response_due_at)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${chargebackStatusClass(cb.status)}`}>
                          {cb.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 py-4 text-center text-sm">No chargebacks on file</p>
          )}
        </CardContent>
      </Card>
      )}

      {selected && confirmingService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Add {confirmingService.name} for {selected.dba_name}?
              </h2>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-gray-700">
                This will provision a new {confirmingService.name} gateway account for {selected.dba_name}. Proceed?
              </p>
              <p className="text-sm text-amber-800">
                The configured environment determines whether this creates a sandbox account or a real billable gateway account.
              </p>
              {gatewayError && (
                <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                  {gatewayError}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <Button
                variant="secondary"
                autoFocus
                onClick={() => {
                  setConfirmingService(null)
                  setGatewayError(null)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => addServiceMutation.mutate({ service: confirmingService })}
                disabled={addServiceMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {addServiceMutation.isPending ? 'Provisioning...' : 'Provision Gateway'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selected && profileService && (
        <GatewayProvisioningProfileModal
          merchant={selected}
          service={profileService}
          form={gatewayProfileForm}
          error={profileError}
          pending={addServiceMutation.isPending}
          onChange={setGatewayProfileForm}
          onCancel={() => {
            setProfileService(null)
            setProfileError(null)
          }}
          onSubmit={() => addServiceMutation.mutate({ service: profileService, provisioningProfile: gatewayProfileForm })}
        />
      )}

      {selected && dejavooService && (
        <DejavooProvisioningModal
          merchantName={selected.dba_name}
          service={dejavooService}
          form={dejavooForm}
          fileBuilderState={dejavooFileBuilderState}
          catalog={dejavooParamCatalogQuery.data ?? null}
          catalogLoading={dejavooParamCatalogQuery.isPending || dejavooParamCatalogQuery.isFetching}
          catalogError={dejavooParamCatalogQuery.error ? apiErrorMessage(dejavooParamCatalogQuery.error, 'Unable to load Dejavoo file-builder catalog.') : null}
          error={dejavooError}
          pending={addServiceMutation.isPending}
          onChange={setDejavooForm}
          onFileBuilderChange={setDejavooFileBuilderState}
          onCancel={() => {
            setDejavooService(null)
            setDejavooError(null)
            setDejavooFileBuilderState(emptyDejavooFileBuilderState())
          }}
          onSubmit={(dejavooConfig) => addServiceMutation.mutate({
            service: dejavooService,
            dejavooProfile: dejavooForm,
            dejavooConfig,
          })}
        />
      )}

      {selected && showAuthorizeNetWizard && (
        <AuthorizeNetBoardingWizard
          merchant={selected}
          onClose={() => setShowAuthorizeNetWizard(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['merchants'] })
            void queryClient.invalidateQueries({ queryKey: ['merchant-deployment-tasks', selectedId] })
            void queryClient.invalidateQueries({ queryKey: ['deployment-tasks'] })
          }}
        />
      )}

      {selected && showAddAssetModal && (
        <EquipmentAssetModal
          merchantName={selected.dba_name}
          editingAsset={editingAsset}
          assetForm={assetForm}
          assetError={assetError}
          addPending={addAssetMutation.isPending}
          updatePending={updateAssetMutation.isPending}
          onChange={setAssetForm}
          onCancel={() => {
            setShowAddAssetModal(false)
            setEditingAsset(null)
            setAssetError(null)
          }}
          onSubmit={() => {
            if (editingAsset) {
              updateAssetMutation.mutate(assetForm)
            } else {
              addAssetMutation.mutate(assetForm)
            }
          }}
        />
      )}

      {selected && showCreateTicketModal && (
        <CreateTicketModal
          context={{
            merchantId: selected.id,
            merchantName: selected.dba_name,
          }}
          onClose={() => setShowCreateTicketModal(false)}
        />
      )}

    </div>
  )
}

interface GatewayProvisioningProfileModalProps {
  merchant: Merchant
  service: GatewayServiceDefinition
  form: GatewayProvisioningProfile
  error: string | null
  pending: boolean
  onChange: (form: GatewayProvisioningProfile) => void
  onCancel: () => void
  onSubmit: () => void
}

interface DejavooProvisioningModalProps {
  merchantName: string
  service: GatewayServiceDefinition
  form: DejavooProfile
  fileBuilderState: DejavooFileBuilderState
  catalog: DejavooParamCatalogResponse | null
  catalogLoading: boolean
  catalogError: string | null
  error: string | null
  pending: boolean
  onChange: (form: DejavooProfile) => void
  onFileBuilderChange: (state: DejavooFileBuilderState) => void
  onCancel: () => void
  onSubmit: (dejavooConfig: Record<string, string>) => void
}

function DejavooProvisioningModal({
  merchantName,
  service,
  form,
  fileBuilderState,
  catalog,
  catalogLoading,
  catalogError,
  error,
  pending,
  onChange,
  onFileBuilderChange,
  onCancel,
  onSubmit,
}: DejavooProvisioningModalProps) {
  const sourceProfile = DEJAVOO_SOURCE_PROFILE_OPTIONS.find((option) =>
    option.device === form.device
    && option.processor === form.processor
    && option.business_type === form.business_type
    && option.pricing === form.pricing
  )
  const comboKey = dejavooComboKey(form)
  const sourceProfileConfigured = Boolean(sourceProfile?.sourceTpn)
  const noSourceProfileMessage = `No Dejavoo source profile configured for ${form.device}/${form.processor}/${form.business_type}/${form.pricing}.`
  const fileBuilderSupported = Boolean(catalog?.supported_combos.includes(comboKey))
  const fileBuilderErrors = catalog && fileBuilderSupported
    ? validateDejavooFileBuilderState(catalog.fields, fileBuilderState, form.processor)
    : {}
  const canSubmit = Boolean(
    sourceProfileConfigured
    && !pending
    && !catalogLoading
    && !catalogError
    && Object.keys(fileBuilderErrors).length === 0,
  )
  const setField = <Key extends keyof DejavooProfile>(field: Key, value: DejavooProfile[Key]) => {
    onChange({ ...form, [field]: value })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4 py-6" onClick={onCancel}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
        <div className="shrink-0 border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Provision {service.name} for {merchantName}</h2>
          <p className="mt-1 text-sm text-gray-500">
            Select the terminal source profile. The merchant TPN is generated automatically during provisioning.
          </p>
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSubmit) return

            onSubmit(
              catalog && fileBuilderSupported
                ? buildDejavooConfigPayload(catalog.fields, fileBuilderState, form.processor)
              : {},
            )
          }}
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Confirming this form can create a Dejavoo terminal in the configured environment.
            </div>

            {error && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {error}
              </p>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Source profile</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <SelectField
                  label="Device"
                  value={form.device}
                  onChange={(value) => setField('device', value as DejavooProfile['device'])}
                  options={[
                    { value: 'qd2', label: 'QD2' },
                    { value: 'qd4', label: 'QD4' },
                  ]}
                  required
                />
                <SelectField
                  label="Processor"
                  value={form.processor}
                  onChange={(value) => setField('processor', value as DejavooProfile['processor'])}
                  options={[
                    { value: 'omaha', label: 'FD Omaha' },
                    { value: 'nashville', label: 'FD Nashville' },
                  ]}
                  required
                />
                <SelectField
                  label="Business type"
                  value={form.business_type}
                  onChange={(value) => setField('business_type', value as DejavooProfile['business_type'])}
                  options={[
                    { value: 'retail', label: 'Retail' },
                    { value: 'restaurant', label: 'Restaurant' },
                  ]}
                  required
                />
                <SelectField
                  label="Pricing"
                  value={form.pricing}
                  onChange={(value) => setField('pricing', value as DejavooProfile['pricing'])}
                  options={[
                    { value: 'interchange', label: 'Interchange' },
                    { value: 'flat_rate', label: 'Flat Rate' },
                  ]}
                  required
                />
              </div>
              <TextField
                label="Description"
                value={form.description}
                maxLength={100}
                onChange={(value) => setField('description', value)}
                hint="Optional Dejavoo terminal description."
              />
              {!sourceProfileConfigured && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {noSourceProfileMessage}
                </p>
              )}
            </section>

            {sourceProfileConfigured && (
              <DejavooFileBuilder
                comboKey={comboKey}
                processor={form.processor}
                catalog={catalog}
                loading={catalogLoading}
                error={catalogError}
                state={fileBuilderState}
                onChange={onFileBuilderChange}
              />
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-5 py-4">
            <Button type="button" variant="secondary" autoFocus onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} className="bg-amber-600 hover:bg-amber-700">
              {pending ? 'Provisioning...' : 'Provision Dejavoo'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function GatewayProvisioningProfileModal({
  merchant,
  service,
  form,
  error,
  pending,
  onChange,
  onCancel,
  onSubmit,
}: GatewayProvisioningProfileModalProps) {
  const requiresNmiProcessor = service.adapterType === 'service_nmi'
  const requiresFullProfile = !merchant.application_id
  const isNashville = form.nmi_platform === 'fdms'
  const setField = (field: keyof GatewayProvisioningProfile, value: string) => {
    onChange({ ...form, [field]: value })
  }

  const normalizedRouting = form.routing_number.replace(/\D+/g, '')
  const normalizedProcessorMid = form.nmi_processor_mid.replace(/\D+/g, '')
  const normalizedMcc = form.nmi_mcc.replace(/\D+/g, '')
  const baseComplete = !requiresFullProfile || (
    form.business_name.trim() &&
    form.business_email.trim() &&
    form.business_phone.trim() &&
    form.address.trim() &&
    form.city.trim() &&
    form.state.trim().length === 2 &&
    form.zip.trim() &&
    form.owner_first_name.trim() &&
    form.owner_last_name.trim() &&
    form.owner_email.trim() &&
    form.owner_phone.trim()
  )
  const nmiProcessorComplete = !requiresNmiProcessor
    || (
      ['fdms', 'fdmso'].includes(form.nmi_platform)
      && normalizedProcessorMid
      && (!isNashville || form.nmi_processor_tid.trim())
      && normalizedMcc.length === 4
    )
  const bankingComplete = !requiresNmiProcessor
    || !requiresFullProfile
    || (form.bank_name.trim() && normalizedRouting.length === 9 && form.account_number.trim())
  const canSubmit = Boolean(baseComplete && bankingComplete && nmiProcessorComplete && !pending)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Required details for {service.name}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {requiresFullProfile
              ? `${merchant.dba_name} was imported without application data. Enter the fields the gateway needs before provisioning.`
              : `Enter the processor details the gateway needs before provisioning ${merchant.dba_name}.`}
          </p>
        </div>
        <form
          className="space-y-5 px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (canSubmit) onSubmit()
          }}
        >
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Confirming this form can create a real gateway account depending on the configured environment.
          </div>

          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}

          {requiresFullProfile && (
            <>
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Business</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField label="Legal/business name" value={form.business_name} onChange={(value) => setField('business_name', value)} required />
                  <TextField label="Business email" type="email" value={form.business_email} onChange={(value) => setField('business_email', value)} required />
                  <TextField label="Business phone" value={form.business_phone} onChange={(value) => setField('business_phone', value)} required />
                  <TextField label="Website" type="url" value={form.website} onChange={(value) => setField('website', value)} />
                </div>
                <TextField label="Business address" value={form.address} onChange={(value) => setField('address', value)} required />
                <div className="grid gap-4 md:grid-cols-3">
                  <TextField label="City" value={form.city} onChange={(value) => setField('city', value)} required />
                  <TextField label="State" value={form.state} maxLength={2} onChange={(value) => setField('state', value.toUpperCase())} required />
                  <TextField label="ZIP" value={form.zip} onChange={(value) => setField('zip', value)} required />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Owner contact</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField label="Owner first name" value={form.owner_first_name} onChange={(value) => setField('owner_first_name', value)} required />
                  <TextField label="Owner last name" value={form.owner_last_name} onChange={(value) => setField('owner_last_name', value)} required />
                  <TextField label="Owner email" type="email" value={form.owner_email} onChange={(value) => setField('owner_email', value)} required />
                  <TextField label="Owner phone" value={form.owner_phone} onChange={(value) => setField('owner_phone', value)} required />
                </div>
              </section>
            </>
          )}

          {requiresNmiProcessor && (
            <>
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Processor</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  <SelectField
                    label="Platform"
                    value={form.nmi_platform}
                    onChange={(value) => onChange({
                      ...form,
                      nmi_platform: value as GatewayProvisioningProfile['nmi_platform'],
                      nmi_processor_tid: value === 'fdmso' ? '' : form.nmi_processor_tid,
                    })}
                    options={[
                      { value: 'fdms', label: 'Nashville' },
                      { value: 'fdmso', label: 'Omaha' },
                    ]}
                    required
                  />
                  <TextField label="Processor MID" value={form.nmi_processor_mid} onChange={(value) => setField('nmi_processor_mid', value)} required />
                  <TextField label="MCC" value={form.nmi_mcc} maxLength={4} onChange={(value) => setField('nmi_mcc', value.replace(/\D+/g, ''))} required />
                  <TextField
                    label="Gateway Username"
                    value={form.nmi_username}
                    maxLength={64}
                    onChange={(value) => setField('nmi_username', value)}
                    hint="Blank auto-generates a username. It cannot be changed after boarding."
                  />
                  {isNashville && (
                    <TextField label="Terminal ID" value={form.nmi_processor_tid} onChange={(value) => setField('nmi_processor_tid', value)} required />
                  )}
                </div>
              </section>

              {requiresFullProfile && (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Banking for NMI underwriting</h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <TextField label="Bank name" value={form.bank_name} onChange={(value) => setField('bank_name', value)} required />
                    <TextField label="Routing number" value={form.routing_number} onChange={(value) => setField('routing_number', value)} required />
                    <TextField label="Account number" value={form.account_number} onChange={(value) => setField('account_number', value)} required />
                  </div>
                </section>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <Button type="button" variant="secondary" autoFocus onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} className="bg-amber-600 hover:bg-amber-700">
              {pending ? 'Provisioning...' : `Provision ${service.name}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  maxLength,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  maxLength?: number
  hint?: string
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <Input
        id={id}
        type={type}
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  )
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  required?: boolean
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}{required ? ' *' : ''}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  )
}
